<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useArdhisasaAuth } from 'dashboard/composables/useArdhisasaAuth';

import ArdhisasaAuthModal from '../tickets/components/ArdhisasaAuthModal.vue';
import ProcessDetailPanel from './components/ProcessDetailPanel.vue';
import {
  getRegistrationByParcel,
  getProcessDetail,
} from 'dashboard/api/ardhisasaLandRecords';
import { getUserDetails } from 'dashboard/api/ardhisasaUsers';

const { t } = useI18n();

// ── Auth ──────────────────────────────────────────────────────────────────────
const {
  showAuth,
  handleAuthenticated,
  dismissAuth,
  store: authStore,
} = useArdhisasaAuth();

const ardhisasaToken = computed(() => authStore.token || '');
const ardhisasaJwt = computed(() => authStore.jwt || '');

// ── Land Registration search ─────────────────────────────────────────────────
const APPLICATION_STATUSES = ['ONGOING', 'COMPLETED', 'REJECTED', 'CANCELLED'];

const parcelNumber = ref('');
const applicationStatus = ref('');

const loading = ref(false);
const error = ref('');
const searched = ref(false);
const parcelResults = ref([]);

function statusClass(status) {
  const map = {
    ONGOING: 'bg-yellow-100 text-yellow-700',
    COMPLETED: 'bg-green-100 text-green-700',
    REJECTED: 'bg-red-100 text-red-700',
    CANCELLED: 'bg-gray-100 text-gray-600',
  };
  return map[status] || 'bg-gray-100 text-gray-600';
}

// user_id -> resolved display name, populated lazily after each parcel search
const actorNames = ref({});

function actorLabel(actor) {
  const resolved = actorNames.value[actor.user_id];
  return `${actor.user_role}: ${resolved || actor.user_id}`;
}

async function resolveActorNames(rows) {
  const userIds = [
    ...new Set(
      rows.flatMap(row => (row.actors || []).map(actor => actor.user_id))
    ),
  ].filter(userId => userId && !actorNames.value[userId]);

  await Promise.all(
    userIds.map(async userId => {
      try {
        const { data } = await getUserDetails(
          ardhisasaToken.value,
          ardhisasaJwt.value,
          userId
        );
        const name =
          [data?.firstname, data?.lastname].filter(Boolean).join(' ') ||
          data?.email;
        if (name) actorNames.value = { ...actorNames.value, [userId]: name };
      } catch {
        // Leave the raw user_id as the fallback label for this actor.
      }
    })
  );
}

async function searchByParcel() {
  if (!parcelNumber.value.trim()) return;
  loading.value = true;
  error.value = '';
  searched.value = true;
  try {
    const { data } = await getRegistrationByParcel(
      ardhisasaToken.value,
      ardhisasaJwt.value,
      parcelNumber.value.trim(),
      applicationStatus.value
    );
    parcelResults.value = Array.isArray(data) ? data : [];
    if (parcelResults.value.some(row => row.actors?.length)) {
      resolveActorNames(parcelResults.value);
    }
  } catch {
    parcelResults.value = [];
    error.value = t('ARDHISASA.LAND_PROCESSES.SEARCH_ERROR');
  } finally {
    loading.value = false;
  }
}

// ── Process detail panel ─────────────────────────────────────────────────────
const selectedProcessName = ref('');
const detailVisible = ref(false);
const detailLoading = ref(false);
const detailError = ref('');
const detailData = ref(null);

async function viewProcess(row) {
  selectedProcessName.value = row.process_name || '';
  detailVisible.value = true;
  detailLoading.value = true;
  detailError.value = '';
  detailData.value = null;
  try {
    const { data } = await getProcessDetail(
      ardhisasaToken.value,
      ardhisasaJwt.value,
      row.process_name,
      row.process_id
    );
    detailData.value = data;
  } catch (err) {
    detailError.value =
      err.response?.data?.error ||
      t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.DETAIL_ERROR');
  } finally {
    detailLoading.value = false;
  }
}

function closeDetail() {
  detailVisible.value = false;
}
</script>

<template>
  <div class="flex flex-col flex-1 w-full h-full overflow-hidden bg-white">
    <ArdhisasaAuthModal
      v-if="showAuth"
      @authenticated="handleAuthenticated"
      @close="dismissAuth"
    />
    <div class="p-6 border-b border-gray-200 flex-shrink-0">
      <h1 class="text-xl font-semibold text-gray-900">
        {{ t('ARDHISASA.LAND_PROCESSES.TITLE') }}
      </h1>
    </div>

    <div class="flex-1 overflow-y-auto p-6 space-y-4">
      <h2 class="text-sm font-semibold text-gray-700">
        {{ t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.TITLE') }}
      </h2>

      <!-- Search inputs -->
      <div class="flex gap-2">
        <input
          v-model="parcelNumber"
          type="text"
          class="flex-1 border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#71481d]"
          :placeholder="
            t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.PARCEL_PLACEHOLDER')
          "
          @keyup.enter="searchByParcel"
        />
        <select
          v-model="applicationStatus"
          class="w-48 flex-shrink-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#71481d]"
        >
          <option value="">
            {{ t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.STATUS_ANY') }}
          </option>
          <option
            v-for="status in APPLICATION_STATUSES"
            :key="status"
            :value="status"
          >
            {{ status }}
          </option>
        </select>
        <button
          class="px-4 py-2 bg-[#71481d] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
          :disabled="loading"
          @click="searchByParcel"
        >
          {{ t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.SEARCH_BUTTON') }}
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-sm text-gray-500">
        {{ t('ARDHISASA.LAND_RECORDS.LOADING') }}
      </div>

      <!-- Error -->
      <div v-if="error" class="text-sm text-red-600">
        {{ error }}
      </div>

      <!-- Empty -->
      <div
        v-if="!loading && !error && searched && !parcelResults.length"
        class="text-sm text-gray-500"
      >
        {{ t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.NO_RESULTS') }}
      </div>

      <!-- Parcel results -->
      <table v-if="!loading && parcelResults.length" class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-200">
            <th class="text-left px-4 py-2 text-xs font-semibold text-gray-500">
              {{
                t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.PARCEL_NUMBER')
              }}
            </th>
            <th class="text-left px-4 py-2 text-xs font-semibold text-gray-500">
              {{ t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.PROCESS_NAME') }}
            </th>
            <th class="text-left px-4 py-2 text-xs font-semibold text-gray-500">
              {{
                t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.REFERENCE_NUMBER')
              }}
            </th>
            <th class="text-left px-4 py-2 text-xs font-semibold text-gray-500">
              {{
                t(
                  'ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.APPLICATION_STATUS'
                )
              }}
            </th>
            <th class="text-left px-4 py-2 text-xs font-semibold text-gray-500">
              {{ t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.ACTORS') }}
            </th>
            <th
              class="text-left px-4 py-2 text-xs font-semibold text-gray-500"
            />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in parcelResults"
            :key="row.process_id"
            class="border-b border-gray-100"
          >
            <td class="px-4 py-2 font-medium text-[#71481d]">
              {{ row.parcel_number }}
            </td>
            <td class="px-4 py-2">{{ row.process_name || '—' }}</td>
            <td class="px-4 py-2">{{ row.reference_number || '—' }}</td>
            <td class="px-4 py-2">
              <span
                v-if="row.application_status"
                class="text-xs font-medium px-2 py-0.5 rounded-full"
                :class="statusClass(row.application_status)"
              >
                {{ row.application_status }}
              </span>
              <span v-else>—</span>
            </td>
            <td class="px-4 py-2">
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="actor in row.actors || []"
                  :key="actor.user_id"
                  class="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
                >
                  {{ actorLabel(actor) }}
                </span>
                <span v-if="!row.actors || !row.actors.length">—</span>
              </div>
            </td>
            <td class="px-4 py-2">
              <button
                class="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                @click="viewProcess(row)"
              >
                {{ t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.VIEW') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ProcessDetailPanel
      v-if="detailVisible"
      :process-name="selectedProcessName"
      :loading="detailLoading"
      :error="detailError"
      :data="detailData"
      @close="closeDetail"
    />
  </div>
</template>
